import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useRights } from './hooks/useRights';
import Login from './components/Login';
import BOLayout from './layouts/BOLayout';
import Courses from './pages/BO/Courses';
import Levels from './pages/BO/Levels';
import Classes from './pages/BO/Classes';
import Teachers from './pages/BO/Teachers';
import Students from './pages/BO/Students';
import ScoreInsert from './pages/BO/ScoreInsert';
import Bulletin from './pages/BO/Bulletin';
import Bills from './pages/BO/Bills';
import Administration from './pages/BO/Administration';
import Timetable from './pages/BO/Timetable';
import TeacherTimetable from './pages/BO/TeacherTimetable';
import StudentTimetable from './pages/BO/StudentTimetable';
import Absences from './pages/BO/Absences';
import StudentDetails from './pages/BO/StudentDetails';
import Schools from './pages/BO/Schools';
import SchoolCreate from './pages/BO/SchoolCreate';
import SchoolEdit from './pages/BO/SchoolEdit';
import LandingPage from './pages/LandingPage';
import { ModalProvider } from './context/ModalContext';
import GlobalModal from './components/GlobalModal';
import NotFound from './pages/NotFound';
import ProfileEdit from './pages/ProfileEdit';
import './App.css';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedTypes?: string[];
  requiredRight?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedTypes, requiredRight }) => {
  const { isAuthenticated, loading, userType } = useAuth();
  const { hasRight, isSuperAdmin } = useRights();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedTypes && userType && !allowedTypes.includes(userType)) {
    return <Navigate to="/login" />;
  }

  // Check rights if required (SuperAdmin bypasses all rights checks)
  if (requiredRight && !isSuperAdmin && !hasRight(requiredRight)) {
    return <Navigate to="/courses" />;
  }

  return <>{children}</>;
};

const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/courses" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  return (
    <AuthProvider>
      <ModalProvider>
        <BrowserRouter>
          <GlobalModal />
          <Routes>
            <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />

            <Route path="/" element={<LandingPage />} />

            <Route
              element={
                <ProtectedRoute allowedTypes={['SUPER_ADMIN', 'ADMIN', 'TEACHER', 'STUDENT']}>
                  <BOLayout />
                </ProtectedRoute>
              }
            >
              <Route path="schools" element={<ProtectedRoute allowedTypes={['SUPER_ADMIN']}><Schools /></ProtectedRoute>} />
              <Route path="schools/create" element={<ProtectedRoute allowedTypes={['SUPER_ADMIN']}><SchoolCreate /></ProtectedRoute>} />
              <Route path="schools/:id/edit" element={<ProtectedRoute allowedTypes={['SUPER_ADMIN']}><SchoolEdit /></ProtectedRoute>} />
              <Route path="courses" element={<ProtectedRoute requiredRight="courses:r"><Courses /></ProtectedRoute>} />
              <Route path="levels" element={<ProtectedRoute requiredRight="levels:r"><Levels /></ProtectedRoute>} />
              <Route path="classes" element={<ProtectedRoute requiredRight="classes:r"><Classes /></ProtectedRoute>} />
              <Route path="teachers" element={<ProtectedRoute requiredRight="teachers:r"><Teachers /></ProtectedRoute>} />
              <Route path="students" element={<ProtectedRoute requiredRight="students:r"><Students /></ProtectedRoute>} />
              <Route path="students/:id" element={<ProtectedRoute requiredRight="students:r"><StudentDetails /></ProtectedRoute>} />
              <Route path="scores/insert" element={<ProtectedRoute requiredRight="scores:r"><ScoreInsert /></ProtectedRoute>} />
              <Route path="bulletin/:classId" element={<ProtectedRoute requiredRight="bulletins:r"><Bulletin /></ProtectedRoute>} />
              <Route path="bills" element={<ProtectedRoute requiredRight="bills:r"><Bills /></ProtectedRoute>} />
              <Route path="administration" element={<ProtectedRoute requiredRight="administration:r"><Administration /></ProtectedRoute>} />
              <Route path="timetable" element={<ProtectedRoute requiredRight="lessons:r" allowedTypes={['ADMIN', 'SUPER_ADMIN']}><Timetable /></ProtectedRoute>} />
              <Route path="timetable/teacher" element={<ProtectedRoute requiredRight="lessons:r" allowedTypes={['TEACHER']}><TeacherTimetable /></ProtectedRoute>} />
              <Route path="timetable/student" element={<ProtectedRoute requiredRight="lessons:r" allowedTypes={['STUDENT']}><StudentTimetable /></ProtectedRoute>} />
              <Route path="absences" element={<ProtectedRoute requiredRight="absences:r"><Absences /></ProtectedRoute>} />
              <Route path="profile" element={<ProtectedRoute allowedTypes={['SUPER_ADMIN', 'ADMIN', 'TEACHER', 'STUDENT']}><ProfileEdit /></ProtectedRoute>} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </ModalProvider>
    </AuthProvider>
  );
};

export default App;
